import { Box, Button, IconButton, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import queryString from 'query-string';
import { PAGE_SIZE } from 'services/constants/env';
import { COLORS } from 'services/constants/colors';
import { IPageResponse } from 'services/constants/types';
import { EasTableWithoutFilter } from 'ui/EasTableWithoutFilter';
import { EasTooltip } from 'ui/EasTooltip';
import { getShopifyStoresRequest, resetShopifyStores } from 'redux/reducers/shopifyApps/reducer';
import { getShopifyStores, getShopifyStoresLoader } from 'redux/reducers/shopifyApps/selectors';
import { TShopifyStore } from 'redux/reducers/shopifyApps/types';
import { THandleAction } from 'services/constants/tableStructureTypes';
import { PATHS } from 'routes/constants';
import { tableStructure, TShopifyStoreTableRow } from './tableStructure';
import { ConnectStoreDialog } from './ConnectStoreDialog';

const ROWS = Array.from(new Array(5));

const REINSTALL_APP_ACTION = 'reinstallApp';

type TDialogState = { open: false } | { open: true; store: TShopifyStore | null };

export const ShopifyDevApp: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { search } = useLocation();
	const { page } = queryString.parse(search);
	const params = useParams<{ appId?: string }>();
	const appId = Number(params.appId);

	const shopifyStores = useSelector(getShopifyStores);
	const shopifyStoresIsLoading = useSelector(getShopifyStoresLoader);

	const [dialog, setDialog] = useState<TDialogState>({ open: false });

	const dispatchGetShopifyStores = () => {
		if (!appId) return;
		dispatch(
			getShopifyStoresRequest({
				appId,
				pageNumber: page ? +page : 1,
				pageSize: PAGE_SIZE,
				filters: [],
				orderBy: [],
				searchString: '',
			}),
		);
	};

	useEffect(() => {
		dispatchGetShopifyStores();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [appId, page]);

	useEffect(() => {
		return () => {
			dispatch(resetShopifyStores());
		};
	}, [dispatch]);

	const tableItems = useMemo((): IPageResponse<TShopifyStoreTableRow> | null => {
		if (!shopifyStores) return null;
		return {
			data: (shopifyStores.data ?? []).map((store) => ({
				...store,
				installLink: !store.installed && store.installUrl ? 'Install App' : null,
			})),
			totalCount: shopifyStores.totalCount ?? 0,
		};
	}, [shopifyStores]);

	const handleAction: THandleAction = (id, type) => {
		if (type !== REINSTALL_APP_ACTION) return;
		const store = shopifyStores?.data?.find((item) => item.id === id);
		if (store) setDialog({ open: true, store });
	};

	return (
		<>
			<EasTableWithoutFilter<TShopifyStoreTableRow>
				tableStructure={tableStructure}
				items={tableItems}
				itemsIsLoading={shopifyStoresIsLoading}
				handleAction={handleAction}
				skeletonRows={ROWS}
				highlightedRow={null}
			>
				<Box
					sx={{
						p: '2px 4px',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<EasTooltip title="Go to the Shopify Dev App list" arrow>
							<IconButton
								onClick={() => navigate(PATHS.SHOPIFY_DEV_APPS_PAGE)}
								color="primary"
								size="small"
							>
								<KeyboardBackspaceIcon />
							</IconButton>
						</EasTooltip>
						<Typography sx={{ fontSize: '1.2rem', color: COLORS.PRIMARY, fontWeight: 'bold' }}>
							{shopifyStores?.app?.name
								? `Shopify Dev App ${shopifyStores.app.name}: Stores`
								: 'Shopify Dev App: Stores'}
						</Typography>
					</Box>
					<Button
						variant="contained"
						size="small"
						startIcon={<Add />}
						disabled={!appId}
						onClick={() => setDialog({ open: true, store: null })}
					>
						Connect Dev App to new Store
					</Button>
				</Box>
			</EasTableWithoutFilter>
			<ConnectStoreDialog
				open={dialog.open}
				appId={appId}
				store={dialog.open ? dialog.store : null}
				onClose={() => setDialog({ open: false })}
				onSuccess={dispatchGetShopifyStores}
			/>
		</>
	);
};
